// config/constants.js - Organization feature constants

/**
 * HTTP Status Codes
 * Standard HTTP response status codes used throughout the organization feature
 */
export const HTTP_STATUS = {
  /** 200 - Success */
  OK: 200,

  /** 201 - Resource created successfully */
  CREATED: 201,

  /** 400 - Bad request (validation error) */
  BAD_REQUEST: 400,

  /** 403 - Forbidden (insufficient permissions) */
  FORBIDDEN: 403,

  /** 404 - Resource not found */
  NOT_FOUND: 404,

  /** 409 - Conflict (resource already exists) */
  CONFLICT: 409,

  /** 429 - Too many requests (rate limit exceeded) */
  TOO_MANY_REQUESTS: 429,

  /** 500 - Internal server error */
  INTERNAL_SERVER_ERROR: 500
};

/**
 * HTTP Headers
 * Custom headers used for API requests
 */
export const HTTP_HEADERS = {
  /** User email header for authentication */
  USER_EMAIL: 'X-User-Email',

  /** Tenant/Organization ID header for multi-tenancy */
  TENANT_ID: 'X-Tenant-Id'
};

/**
 * Sentinel Values
 * Special string values with semantic meaning in the application
 */
export const SENTINEL_VALUES = {
  /** Indicates that a division or group should be removed/cleared */
  REMOVE: 'REMOVE'
};

/**
 * Member Status Values
 * Valid status values for organization members
 */
export const MEMBER_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  PENDING: 'PENDING'
};

/**
 * Error Messages
 * Standardized error messages for common scenarios
 */
export const ERROR_MESSAGES = {
  CONFLICT: 'A resource with this name already exists',
  RATE_LIMIT: 'Too many requests. Please try again later',
  FORBIDDEN: 'You do not have permission to perform this action',
  NOT_FOUND: 'The requested resource was not found',
  BAD_REQUEST: 'Invalid request. Please check your input',
  INTERNAL_SERVER_ERROR: 'An unexpected error occurred. Please try again later'
};

/**
 * API Timeouts (in milliseconds)
 * Timeout values for different types of API operations
 */
export const API_TIMEOUTS = {
  /** Standard API request timeout (30 seconds) */
  STANDARD: 30000,

  /** Bulk operation timeout (45 seconds) - longer for batch updates */
  BULK_OPERATION: 45000
};

/**
 * UI Delays (in milliseconds)
 * Delay values for UI operations and retries
 */
export const UI_DELAYS = {
  /** Retry delay for failed data loads (2 seconds) */
  RETRY_DELAY: 2000,

  /** Short delay for UI transitions (500ms) */
  TRANSITION_DELAY: 500
};

/**
 * Form Validation Rules
 * Character limits and patterns for organization form inputs
 */
export const VALIDATION = {
  /** Roles, divisions, teams - entity names */
  ENTITY_NAME: { minLength: 2, maxLength: 30 },
  /** First name, last name - person names */
  PERSON_NAME: { minLength: 1, maxLength: 50 },
  /** Descriptions for roles, divisions, teams */
  DESCRIPTION: { maxLength: 250 },
  /** Email addresses (RFC 5321 max) */
  EMAIL: { maxLength: 254 },
  /** Characters blocked in all text inputs (XSS vectors) */
  BLOCKED_CHARS: /[<>{}";`\\|]/,
  /** Person names: letters, spaces, hyphens, apostrophes (supports accented chars) */
  PERSON_NAME_PATTERN: /^[a-zA-ZÀ-ÿ\s'-]+$/,
  /** Entity names: letters, spaces, hyphens, underscores (matches backend security policy) */
  ENTITY_NAME_PATTERN: /^[a-zA-ZÀ-ÿ\s\-_]+$/,
  /** Descriptions: letters, numbers, spaces, and basic punctuation only (no special chars) */
  DESCRIPTION_PATTERN: /^[a-zA-ZÀ-ÿ0-9\s.,'?]+$/
};

/**
 * Dangerous Input Patterns
 * Regex patterns that detect XSS, injection, and other malicious input
 */
export const DANGEROUS_PATTERNS = [
  /<script[\s\S]*?<\/script>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi,
  /<iframe[\s\S]*?>/gi,
  /<object[\s\S]*?>/gi,
  /<embed[\s\S]*?>/gi,
  /data:\s*text\/html/gi,
  /<style[\s\S]*?<\/style>/gi,
  /<link[\s\S]*?>/gi,
  /expression\s*\(/gi,
  /url\s*\(/gi,
  /import\s+/gi,
  /eval\s*\(/gi,
  /function\s*\(/gi
];
